export const HOME_PATH = '/'

type ScheduleNonCriticalWorkOptions = {
  timeoutMs?: number
}

export function isGuestHomePath(pathname: string | null): boolean {
  return pathname == null || pathname === HOME_PATH
}

export function schedulePathAwareNonCriticalWork(
  pathname: string | null,
  callback: () => void,
  options?: ScheduleNonCriticalWorkOptions
): () => void {
  if (isGuestHomePath(pathname)) {
    return scheduleUserIntentWork(callback)
  }

  return scheduleNonCriticalWork(callback, options)
}

function attachUserIntentListeners(onUserIntent: () => void): () => void {
  window.addEventListener('scroll', onUserIntent, {
    once: true,
    passive: true,
    capture: true
  })
  window.addEventListener('pointerdown', onUserIntent, {
    once: true,
    capture: true
  })
  window.addEventListener('keydown', onUserIntent, {
    once: true,
    capture: true
  })

  return () => {
    window.removeEventListener('scroll', onUserIntent, { capture: true })
    window.removeEventListener('pointerdown', onUserIntent, { capture: true })
    window.removeEventListener('keydown', onUserIntent, { capture: true })
  }
}

export function scheduleUserIntentWork(callback: () => void): () => void {
  let cancelled = false

  const run = () => {
    if (!cancelled) {
      callback()
    }
  }

  const detachUserIntent = attachUserIntentListeners(run)

  return () => {
    cancelled = true
    detachUserIntent()
  }
}

export function scheduleNonCriticalWork(
  callback: () => void,
  options?: ScheduleNonCriticalWorkOptions
): () => void {
  let cancelled = false

  const run = () => {
    if (!cancelled) {
      callback()
    }
  }

  const timeoutMs = options?.timeoutMs ?? 3500
  const idleHandle =
    typeof requestIdleCallback !== 'undefined'
      ? requestIdleCallback(run, { timeout: timeoutMs })
      : setTimeout(run, timeoutMs)

  const detachUserIntent = attachUserIntentListeners(run)

  return () => {
    cancelled = true
    detachUserIntent()
    if (
      typeof cancelIdleCallback !== 'undefined' &&
      typeof idleHandle === 'number'
    ) {
      cancelIdleCallback(idleHandle)
    } else {
      clearTimeout(idleHandle as ReturnType<typeof setTimeout>)
    }
  }
}
